import { adjustedCategory, categoryLabel } from "../config/productCategories";
import { memo, useCallback, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowRight, BadgeCheck, ChevronRight, Clock, EyeOff, Flame, Heart, Lock, MapPin, MessageCircle, Navigation, Package } from "lucide-react";
import type { ProductListItem } from "../types/Product";
import type { CommunitySuccessPost } from "../services/CommunitySuccessService";
import { getProductImageUrl } from "../utils/imageUtils";
import {
  type TopProductsViewModel,
  getTopProductPrice as getEffectivePrice,
  getTopProductOriginalPrice as getOriginalPrice,
  getTopProductDiscountPct as getDiscountPct,
  isNew24h,
  isAtLowestPrice,
} from "../utils/topProducts";
import SaveButton from "./buttons/SaveButton";
import CheckStockButton from "./buttons/CheckStockButton";
import ProductDetailModal from "./ProductDetailModal";
import { getProductDetailIdentifier } from "./ProductDetailModal.utils";
import { ONLINE_ONLY_RETAILERS } from "../config/retailers";
import { getOpenBoxConditionLabel, getRetailerLookupSku } from "../utils/openBox";
import { useTranslation } from "../context/LanguageContext";
import { samsClubStockLabel } from "../utils/stockCount";

interface StoreOption {
  id: string;
  name: string;
  isAll?: boolean;
}

interface TopProductsViewProps {
  model: TopProductsViewModel;
  totalAllCount: number;
  storeFilterOptions?: StoreOption[];
  onSeeAll: () => void;
  onSeeAllNew?: () => void;
  onSelectCategory?: (category: string) => void;
  onCheckStockSearch?: (params: {
    searchCode: string;
    searchType: "UPC" | "SKU";
    fallbackCode?: string;
    fallbackType?: "SKU";
  }) => void | Promise<void>;
  retailer: string;
  onHideProduct?: (product: ProductListItem) => void;
  isFreeUser?: boolean;
  communityPosts?: CommunitySuccessPost[];
  communityLoading?: boolean;
  onOpenCommunity?: () => void;
  onUpgrade?: () => void;
  /** "grid" lays each section out as cards in rows; "list" keeps the sideways rails. */
  layoutMode?: "list" | "grid";
}

const ONLINE_RETAILERS = new Set<string>(ONLINE_ONLY_RETAILERS);
const HOT_DEAL_DISCOUNT_THRESHOLD = 70;
const isOnlineDeal = (p: ProductListItem, retailer: string) =>
  p.storeId === "online" || ONLINE_RETAILERS.has(retailer);
const showNearbyFor = (p: ProductListItem, retailer: string) =>
  retailer !== "woot" && retailer !== "lowes" && !isOnlineDeal(p, retailer);
const fmtPrice = (n: number) => (n >= 100 ? `$${n.toFixed(0)}` : `$${n.toFixed(2)}`);
type Translate = (key: string, params?: Record<string, string | number>) => string;
interface DetailTarget {
  sku: string;
  storeId: string;
  retailer: string;
}

function TopProductsViewImpl({
  model,
  totalAllCount,
  storeFilterOptions = [],
  onSeeAll,
  onSeeAllNew,
  onSelectCategory,
  onCheckStockSearch,
  retailer,
  onHideProduct,
  isFreeUser = false,
  communityPosts = [],
  communityLoading = false,
  onOpenCommunity,
  onUpgrade,
  layoutMode = "list",
}: TopProductsViewProps) {
  const grid = layoutMode === "grid";
  const { t } = useTranslation();
  const [detail, setDetail] = useState<DetailTarget | null>(null);
  const [detailAnim, setDetailAnim] = useState(false);

  const { stats, hasRecent, featured, picks, hoursWindow } = model;

  const storeLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of storeFilterOptions) if (!s.isAll) map.set(s.id, s.name);
    return map;
  }, [storeFilterOptions]);
  const resolveStoreLabel = useCallback(
    (storeId?: string) => {
      const raw = (storeId || "").trim();
      if (!raw || raw === "online") return null;
      const first = raw.split(",")[0].trim();
      return storeLabelById.get(first) || t("topFeed.storeFallback", { id: first });
    },
    [storeLabelById, t]
  );

  const handleOpen = useCallback((p: ProductListItem) => {
    const sku = getProductDetailIdentifier(p, retailer);
    if (!sku) return;
    const isOnline = isOnlineDeal(p, retailer);
    setDetail({
      sku,
      storeId: isOnline ? "online" : p.storeId,
      retailer: retailer === "walmart_online" ? "walmart" : retailer,
    });
    setTimeout(() => setDetailAnim(true), 10);
  }, [retailer]);
  const handleCloseDetail = useCallback(() => {
    setDetailAnim(false);
    setTimeout(() => setDetail(null), 300);
  }, []);

  return (
    <div
      data-products-scroll-container
      className={`w-full h-full overflow-y-auto scrollbar-modern overscroll-contain pb-24 dark:bg-dark-950 ${grid ? "bg-[#F9F8FC]" : "bg-[#f7f5fa]"}`}
    >
      <div className="mx-auto w-full max-w-5xl">
        <SectionHeader
          title={hasRecent ? t("topFeed.todaysTopDeals") : t("topFeed.bestDealsNow")}
          subtitle={hasRecent
            ? t("topFeed.freshSubtitle", { count: stats.recentCount.toLocaleString(), pct: stats.avgDiscount })
            : t("topFeed.bestMarkdownsNow")}
          action={totalAllCount > 0 ? { label: t("topFeed.seeAll"), onClick: onSeeAll } : undefined}
          large
        />
        {featured.length > 0 && (
          <DealRail
            products={featured}
            retailer={retailer}
            resolveStoreLabel={resolveStoreLabel}
            onOpen={handleOpen}
            onCheckStockSearch={onCheckStockSearch}
            onHideProduct={onHideProduct}
            hottestFirst
            grid={grid}
          />
        )}

        {isFreeUser && (
          <CommunitySuccessPreview
            post={communityPosts[0]}
            loading={communityLoading}
            onOpen={onOpenCommunity}
            onUpgrade={onUpgrade}
          />
        )}

        {hasRecent && stats.justDropped.length > 0 && (
          <div className="mt-7">
            <SectionHeader
              title={t("topFeed.justDropped")}
              subtitle={t("topFeed.addedInLastHours", { count: stats.justDroppedCount, hours: hoursWindow })}
              action={onSeeAllNew ? { label: t("topFeed.seeAllNew"), onClick: onSeeAllNew } : undefined}
            />
            <DealRail
              products={stats.justDropped}
              retailer={retailer}
              resolveStoreLabel={resolveStoreLabel}
              onOpen={handleOpen}
              onCheckStockSearch={onCheckStockSearch}
              onHideProduct={onHideProduct}
              grid={grid}
            />
          </div>
        )}

        {hasRecent && stats.categoryRails.slice(0, 3).map((rail) => (
          <div key={`cat-${categoryLabel(rail.category)}`} className="mt-7">
            <SectionHeader
              title={t("topFeed.topCategory", { category: categoryLabel(rail.category) })}
              subtitle={t("topFeed.railSubtitle", { count: rail.count, pct: rail.avg })}
              action={onSelectCategory ? { label: t("topFeed.seeAll"), onClick: () => onSelectCategory(rail.category) } : undefined}
            />
            <DealRail
              products={rail.items}
              retailer={retailer}
              resolveStoreLabel={resolveStoreLabel}
              onOpen={handleOpen}
              onCheckStockSearch={onCheckStockSearch}
              onHideProduct={onHideProduct}
              grid={grid}
            />
          </div>
        ))}

        {picks.length > 0 && (
          <div className="mt-7">
            <SectionHeader title={t("topFeed.moreTopPicks")} />
            <DealRail
              products={picks}
              retailer={retailer}
              resolveStoreLabel={resolveStoreLabel}
              onOpen={handleOpen}
              onCheckStockSearch={onCheckStockSearch}
              onHideProduct={onHideProduct}
              grid={grid}
            />
          </div>
        )}

        {totalAllCount > 0 && (
          <button
            type="button"
            onClick={onSeeAll}
            className="w-full flex items-center justify-center gap-1.5 py-4 mt-7 text-sm font-bold text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors cursor-pointer"
          >
            <span>{t("topFeed.browseAllDeals", { count: totalAllCount.toLocaleString() })}</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {detail &&
        createPortal(
          <div
            className={`fixed inset-0 z-[10000] flex items-end justify-center transition-colors duration-[250ms] ease-out motion-reduce:transition-none ${
              detailAnim ? "bg-black/50" : "bg-black/0"
            }`}
            onPointerDown={(e) => {
              if (e.target === e.currentTarget) handleCloseDetail();
            }}
          >
            <div
              className={`bg-white dark:bg-dark-900 w-full h-[75vh] rounded-t-xl overflow-hidden flex flex-col transform transition-all duration-300 ease-out ${
                detailAnim ? "translate-y-0" : "translate-y-full"
              }`}
              onPointerDown={(e) => e.stopPropagation()}
            >
              <ProductDetailModal
                sku={detail.sku}
                storeId={detail.storeId}
                retailer={detail.retailer}
                onClose={handleCloseDetail}
              />
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

function CommunitySuccessPreview({
  post,
  loading,
  onOpen,
  onUpgrade,
}: {
  post?: CommunitySuccessPost;
  loading: boolean;
  onOpen?: () => void;
  onUpgrade?: () => void;
}) {
  const { t } = useTranslation();
  if (loading) {
    return <div className="mx-3 mt-5 h-40 animate-pulse rounded-[22px] bg-white/80 dark:bg-white/5" aria-label={t("topFeed.loadingCommunity")} />;
  }

  return (
    <section className="mx-3 mt-5 overflow-hidden rounded-[22px] border border-violet-200 bg-white dark:border-violet-500/25 dark:bg-[#211d1b]">
      <div className="flex items-center justify-between gap-3 border-b border-violet-100 bg-violet-50/70 px-4 py-3 dark:border-violet-500/15 dark:bg-violet-500/10">
        <div>
          <p className="flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-[0.12em] text-primary-700 dark:text-primary-300">
            <BadgeCheck className="h-3.5 w-3.5" /> {t("topFeed.communitySuccess")}
          </p>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-dark-300">{t("topFeed.communitySubtitle")}</p>
        </div>
        <button type="button" onClick={onOpen} className="flex items-center gap-0.5 text-xs font-extrabold text-primary-700 dark:text-primary-300">
          {t("topFeed.seeAll")} <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>

      {post ? (
        <button type="button" onClick={onOpen} className="grid w-full grid-cols-[minmax(0,1fr)_116px] gap-3 p-4 text-left sm:grid-cols-[minmax(0,1fr)_160px]">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {post.authorAvatarUrl ? (
                <img src={post.authorAvatarUrl} alt="" className="h-9 w-9 rounded-full object-cover" />
              ) : (
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-700 text-[11px] font-black text-white">{getInitials(post.authorName)}</div>
              )}
              <div className="min-w-0">
                <p className="truncate text-sm font-extrabold text-gray-900 dark:text-white">{post.authorName}</p>
                <p className="text-[11px] text-gray-500 dark:text-dark-300">{formatRelativeTime(post.createdAt, t)}{post.source === "discord" ? " · Discord" : ""}</p>
              </div>
            </div>
            {post.message && post.message !== "Shared from the DealHawk Discord community." && (
              <p className="mt-3 line-clamp-3 text-sm font-medium leading-5 text-gray-700 dark:text-dark-100">“{post.message}”</p>
            )}
            <div className="mt-3 flex items-center gap-4 text-xs font-semibold text-gray-500 dark:text-dark-300">
              <span className="flex items-center gap-1"><Heart className="h-3.5 w-3.5" /> {post.likeCount || 0}</span>
              <span className="flex items-center gap-1"><MessageCircle className="h-3.5 w-3.5" /> {post.commentCount || 0}</span>
            </div>
          </div>
          <img src={(post.imageUrls?.length ? post.imageUrls[0] : post.imageUrl)} alt={t("topFeed.communityDealFind")} className="h-28 w-full rounded-2xl bg-gray-100 object-cover dark:bg-dark-800" />
        </button>
      ) : (
        <button type="button" onClick={onOpen} className="flex w-full items-center gap-3 p-4 text-left">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary-100 text-primary-700 dark:bg-primary-500/15 dark:text-primary-300"><MessageCircle className="h-5 w-5" /></div>
          <div><p className="text-sm font-extrabold text-gray-900 dark:text-white">{t("topFeed.communityFree")}</p><p className="mt-0.5 text-xs text-gray-500 dark:text-dark-300">{t("topFeed.communityFreeSub")}</p></div>
        </button>
      )}

      <div className="flex items-center justify-between gap-3 border-t border-violet-100 px-4 py-2.5 text-xs dark:border-violet-500/15">
        <span className="flex items-center gap-1.5 font-semibold text-gray-500 dark:text-dark-300"><Lock className="h-3.5 w-3.5" /> {t("topFeed.freeToBrowse")}</span>
        <button type="button" onClick={onUpgrade} className="font-extrabold text-primary-700 dark:text-primary-300">{t("topFeed.upgradeToShare")}</button>
      </div>
    </section>
  );
}

const getInitials = (name: string) => name.split(/\s+/).map((word) => word[0]).join("").slice(0, 2).toUpperCase() || "DH";
const formatRelativeTime = (timestamp: number, t: Translate) => {
  const diff = Math.max(0, Date.now() - timestamp);
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return t("topFeed.justNow");
  if (minutes < 60) return t("topFeed.minutesAgo", { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t("topFeed.hoursAgo", { count: hours });
  const days = Math.floor(hours / 24);
  return days < 7 ? t("topFeed.daysAgo", { count: days }) : new Date(timestamp).toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const TopProductsView = memo(TopProductsViewImpl);
export default TopProductsView;

const SectionHeader = memo(function SectionHeader({
  title,
  subtitle,
  eyebrow,
  action,
  large,
}: {
  title: string;
  subtitle?: string;
  eyebrow?: string;
  action?: { label: string; onClick: () => void };
  large?: boolean;
}) {
  return (
    <div className={`flex items-end gap-3 px-4 mb-2.5 ${large ? "pt-5 mb-3.5" : ""}`}>
      <div className="flex-1 min-w-0">
        {eyebrow && (
          <div className="text-[10px] font-bold tracking-[0.12em] uppercase text-gray-500 dark:text-dark-400 mb-1">
            {eyebrow}
          </div>
        )}
        <div
          className={`font-extrabold text-gray-900 dark:text-white ${
            large ? "text-[24px] leading-7" : "text-[17px]"
          }`}
        >
          {title}
        </div>
        {subtitle && (
          <div className="text-xs text-gray-500 dark:text-dark-400 mt-1">{subtitle}</div>
        )}
      </div>
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          className="flex items-center gap-0.5 py-1 text-[13px] font-bold text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 max-w-[60%] truncate cursor-pointer"
        >
          <span className="truncate">{action.label}</span>
          <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" />
        </button>
      )}
    </div>
  );
});

interface DealRailProps {
  products: ProductListItem[];
  retailer: string;
  resolveStoreLabel: (storeId?: string) => string | null;
  onOpen: (p: ProductListItem) => void;
  onCheckStockSearch?: TopProductsViewProps["onCheckStockSearch"];
  onHideProduct?: TopProductsViewProps["onHideProduct"];
  getTimeBadge?: (p: ProductListItem) => string | undefined;
  hottestFirst?: boolean;
  grid?: boolean;
}

// Grid rows: up to 8 cards, trimmed to an even count so phones show full rows of two.
const GRID_LIMIT = 8;

function DealRail({
  products,
  retailer,
  resolveStoreLabel,
  onOpen,
  onCheckStockSearch,
  onHideProduct,
  getTimeBadge,
  hottestFirst = false,
  grid = false,
}: DealRailProps) {
  if (grid) {
    const cards = products.slice(0, GRID_LIMIT);
    const even = cards.length > 1 && cards.length % 2 === 1 ? cards.slice(0, -1) : cards;
    return (
      <div className="grid grid-cols-2 gap-2.5 px-3 pb-2 sm:grid-cols-3 lg:grid-cols-4">
        {even.map((p, i) => (
          <DealCard
            key={`${p.upc || p.sku || "p"}-${i}`}
            product={p}
            retailer={retailer}
            hottest={hottestFirst && i === 0}
            timeBadge={getTimeBadge?.(p)}
            storeLabel={isOnlineDeal(p, retailer) ? null : resolveStoreLabel(p.storeId)}
            onOpen={() => onOpen(p)}
            onCheckStockSearch={onCheckStockSearch}
            onHideProduct={onHideProduct}
            fluid
          />
        ))}
      </div>
    );
  }
  return (
    <div
      className="flex items-stretch gap-2.5 overflow-x-auto overflow-y-hidden overscroll-x-contain px-4 pt-0.5 pb-2 snap-x scroll-px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {products.map((p, i) => (
        <DealCard
          key={`${p.upc || p.sku || "p"}-${i}`}
          product={p}
          retailer={retailer}
          hottest={hottestFirst && i === 0}
          timeBadge={getTimeBadge?.(p)}
          storeLabel={isOnlineDeal(p, retailer) ? null : resolveStoreLabel(p.storeId)}
          onOpen={() => onOpen(p)}
          onCheckStockSearch={onCheckStockSearch}
          onHideProduct={onHideProduct}
        />
      ))}
    </div>
  );
}

interface DealCardProps {
  product: ProductListItem;
  retailer: string;
  hottest?: boolean;
  timeBadge?: string;
  storeLabel: string | null;
  onOpen: () => void;
  onCheckStockSearch?: TopProductsViewProps["onCheckStockSearch"];
  onHideProduct?: TopProductsViewProps["onHideProduct"];
  /** Fill the grid cell instead of the rail's fixed width. */
  fluid?: boolean;
}

const DealCard = memo(function DealCard({
  product,
  retailer,
  hottest = false,
  timeBadge,
  storeLabel,
  onOpen,
  onCheckStockSearch,
  onHideProduct,
  fluid = false,
}: DealCardProps) {
  const { t } = useTranslation();
  const discount = getDiscountPct(product);
  const isHotDeal = discount >= HOT_DEAL_DISCOUNT_THRESHOLD;
  const orig = getOriginalPrice(product);
  const price = getEffectivePrice(product);
  const onlineDeal = isOnlineDeal(product, retailer);
  const showNearby = showNearbyFor(product, retailer);
  const latestSf = product.salesFloor?.length ? product.salesFloor[product.salesFloor.length - 1] : 0;
  const latestBr = product.backroom?.length ? product.backroom[product.backroom.length - 1] : 0;
  const stockLeft = Math.max(0, latestSf) + Math.max(0, latestBr);
  const stockChip = onlineDeal
    ? null
    : stockLeft > 0
    ? samsClubStockLabel(retailer, stockLeft, { inStock: t("topFeed.inStock"), lowStock: t("topFeed.lowStock") }) ?? t("topFeed.left", { count: stockLeft })
    : product.inStock === false
    ? t("topFeed.checkStock")
    : t("topFeed.inStock");
  const aisle = onlineDeal ? undefined : product.aisle?.trim();
  const isOpenBox = retailer === "bestbuy" && Boolean(product.openBox);
  const openBoxCondition = isOpenBox ? getOpenBoxConditionLabel(product.openBoxCondition, (key) => t(key)) : "";
  const where = onlineDeal
    ? product.sellerName ? t("topFeed.onlineSoldBy", { seller: product.sellerName }) : t("topFeed.online")
    : storeLabel;
  const isNew = isNew24h(product);
  const isLowest = isAtLowestPrice(product);
  const stop = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <div
      onClick={onOpen}
      className={`group cursor-pointer ${fluid ? "w-full min-w-0" : "w-[204px] flex-shrink-0 snap-start"} rounded-[20px] border bg-white dark:bg-[#26221f] overflow-hidden flex flex-col ${
        hottest
          ? "border-purple-200 dark:border-purple-400/40 shadow-[0_2px_12px_rgba(106,13,173,0.10)]"
          : "border-gray-900/[0.07] dark:border-white/[0.07] shadow-[0_1px_2px_rgba(16,24,40,0.04),0_2px_10px_rgba(16,24,40,0.05)]"
      } dark:shadow-[0_1px_3px_rgba(0,0,0,0.3)]`}
    >
      <div className="relative aspect-square bg-white flex-shrink-0 overflow-hidden">
        {product.image ? (
          <img
            src={getProductImageUrl(product.image, "300")}
            alt=""
            loading="lazy"
            className="absolute inset-0 w-full h-full object-contain p-3"
          />
        ) : null}

        {isNew ? (
          <span className="absolute top-2.5 left-2.5 rounded-full bg-emerald-500 px-2 py-[3px] text-[10px] font-extrabold tracking-wider text-white">
            {t("topFeed.badgeNew")}
          </span>
        ) : isLowest ? (
          <span className="absolute top-2.5 left-2.5 rounded-full bg-violet-600 px-2 py-[3px] text-[10px] font-extrabold tracking-wider text-white">
            {t("topFeed.badgeLowest")}
          </span>
        ) : null}

        {timeBadge && (
          <div className="absolute bottom-2 right-2 flex items-center gap-1 px-1.5 py-[2.5px] rounded-full bg-gray-900/70 text-white text-[10px] font-bold">
            <Clock className="w-2.5 h-2.5" />
            <span>{timeBadge}</span>
          </div>
        )}

        <div onClick={stop} className="absolute top-2 right-2 flex items-center gap-1">
          {onHideProduct && (
            <button
              type="button"
              onClick={() => onHideProduct(product)}
              className="hidden h-[30px] w-[30px] items-center justify-center rounded-full bg-white/95 text-gray-500 shadow-[0_1px_4px_rgba(0,0,0,0.12)] transition-colors hover:text-gray-900 [@media(hover:hover)]:group-hover:flex focus-visible:flex"
              aria-label={t("products.hideProductNamed", { name: product.name })}
              title={t("products.hideProduct")}
            >
              <EyeOff className="h-3.5 w-3.5" />
            </button>
          )}
          <div className="rounded-full bg-rose-50 shadow-[0_1px_4px_rgba(0,0,0,0.10)]">
            <SaveButton
              productId={product.upc || product.sku || ""}
              productName={product.name || ""}
              productImage={product.image}
              productCategory={adjustedCategory(product)}
              productStoreId={product.storeId}
              compact
            />
          </div>
        </div>
      </div>

      <div className="px-3 pt-2 pb-2.5 flex-1 flex flex-col gap-1">
        <div
          className="text-[14px] font-semibold leading-[18px] text-gray-900 dark:text-white overflow-hidden"
          style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", minHeight: 36 }}
        >
          {product.name}
        </div>
        <div className="flex items-baseline flex-wrap gap-x-1.5">
          <span className="text-[20px] font-extrabold tracking-tight text-emerald-600 dark:text-emerald-400">
            {fmtPrice(price)}
          </span>
          {orig > price && (
            <span className="text-[13px] text-gray-400 dark:text-dark-400 line-through">
              {fmtPrice(orig)}
            </span>
          )}
        </div>
        {discount > 0 && (
          <div className="flex items-center gap-1.5">
            <span className={`text-[14px] font-extrabold ${isHotDeal ? "text-orange-600 dark:text-orange-400" : "text-primary-600 dark:text-primary-300"}`}>
              {t("topFeed.percentOffShort", { pct: discount })}
            </span>
            {isHotDeal && (
              <span className="inline-flex items-center gap-0.5 rounded-full bg-orange-100 px-2 py-[2px] text-[10px] font-extrabold tracking-wide text-orange-700 dark:bg-orange-500/15 dark:text-orange-300">
                <Flame className="h-2.5 w-2.5" /> {t("topFeed.hot")}
              </span>
            )}
          </div>
        )}
        {where && (
          <div className="flex items-center gap-1 min-w-0">
            <MapPin className="w-3 h-3 text-gray-400 dark:text-dark-400 flex-shrink-0" />
            <span className="text-[12px] font-medium text-gray-500 dark:text-dark-400 truncate">{where}</span>
          </div>
        )}
        {(stockChip || aisle || isOpenBox) && (
          <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
            {isOpenBox && (
              <span className="inline-flex items-center gap-1 rounded-md bg-amber-100 px-1.5 py-[3px] text-[11px] font-bold text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                <Package className="h-3 w-3" /> {openBoxCondition ? t("card.openBoxCondition", { condition: openBoxCondition }) : t("products.openBoxLabel")}
              </span>
            )}
            {stockChip && (
              <span className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-1.5 py-[3px] text-[11px] font-bold text-gray-700 dark:bg-white/[0.07] dark:text-dark-200">
                <Package className="h-3 w-3" /> {stockChip}
              </span>
            )}
            {aisle && (
              <span className="inline-flex items-center gap-1 rounded-md bg-blue-50 px-1.5 py-[3px] text-[11px] font-bold text-blue-700 dark:bg-blue-500/15 dark:text-blue-300" title={t("topFeed.aisle", { aisle })}>
                <Navigation className="h-3 w-3" /> {aisle}
              </span>
            )}
          </div>
        )}
      </div>

      {showNearby && (
        <div onClick={stop} className="border-t border-gray-100 px-2.5 pt-2 pb-2.5 dark:border-white/[0.06]">
          <CheckStockButton
            productSku={getRetailerLookupSku(product) || product.upc || ""}
            productUpc={product.upc || product.sku}
            onSearch={onCheckStockSearch}
            label={t("topFeed.nearby")}
            className="!h-[36px] !w-full !min-w-0 !inline-flex !items-center !justify-center !gap-1.5 !rounded-xl !border-0 !shadow-none !text-[14px] !font-semibold !py-0 !px-2 !bg-emerald-50 dark:!bg-emerald-500/10 !text-emerald-700 dark:!text-emerald-300 hover:!bg-emerald-100 dark:hover:!bg-emerald-500/20"
          />
        </div>
      )}
    </div>
  );
});
